/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  format,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  isBefore,
  startOfWeek,
  startOfMonth,
  isSameMonth,
  isThisMonth,
} from "date-fns";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import Text from "../ui/Text";
import FormatDateTime from "../formatters/FormatDateTime";

const StyledCalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const CalendarHeaderMonth = styled.div`
  padding: 7px 16px;
`;

const CalendarHeaderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CalendarHeader = ({ date, setCurrentDate, isWeekView, maxDate }) => {
  const [lastCurrentMonthDate, setLastCurrentMonthDate] = useState(date);

  useEffect(() => {
    if (isThisMonth(date)) {
      setLastCurrentMonthDate(date);
    }
  }, [date]);

  const isPrevDisabled = isWeekView
    ? isBefore(startOfWeek(date), startOfWeek(new Date()))
    : isSameMonth(date, new Date());

  // Disable "next" once the whole next view falls after the membership coverage end.
  const isNextDisabled =
    !!maxDate &&
    format(isWeekView ? startOfWeek(addWeeks(date, 1)) : startOfMonth(addMonths(date, 1)), "yyyy-MM-dd") > maxDate;

  const nextMonth = () => {
    const currentDate = new Date(date);
    const nextDate = addMonths(currentDate, 1);

    if (isThisMonth(nextDate)) {
      setCurrentDate(format(new Date(lastCurrentMonthDate), "yyyy-MM-dd"));
    } else {
      setCurrentDate(format(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1), "yyyy-MM-dd"));
    }
  };

  const prevMonth = () => {
    const currentDate = new Date(date);
    const prevDate = subMonths(currentDate, 1);

    if (isThisMonth(prevDate)) {
      setCurrentDate(format(new Date(lastCurrentMonthDate), "yyyy-MM-dd"));
    } else {
      setCurrentDate(format(new Date(prevDate.getFullYear(), prevDate.getMonth(), 1), "yyyy-MM-dd"));
    }
  };

  const nextWeek = () => {
    const currentDate = new Date(date);
    setCurrentDate(format(addWeeks(currentDate, 1), "yyyy-MM-dd"));
  };

  const prevWeek = () => {
    const currentDate = new Date(date);
    setCurrentDate(format(subWeeks(currentDate, 1), "yyyy-MM-dd"));
  };

  return (
    <StyledCalendarHeader>
      <CalendarHeaderMonth>
        <Text color="dark" weight="bold">
          <FormatDateTime value={date} format={FormatDateTime.FORMAT.MONTH} />
        </Text>
      </CalendarHeaderMonth>

      <CalendarHeaderControls>
        <Button
          variant="transparent"
          size="icon"
          icon={<Icon.ChevronLeft />}
          onClick={isWeekView ? prevWeek : prevMonth}
          disabled={isPrevDisabled}
        />

        <Button
          variant="transparent"
          size="icon"
          icon={<Icon.ChevronRight />}
          onClick={isWeekView ? nextWeek : nextMonth}
          disabled={isNextDisabled}
        />
      </CalendarHeaderControls>
    </StyledCalendarHeader>
  );
};

export default CalendarHeader;
